import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope,
  FlaskConical,
  ArrowRight,
  GraduationCap,
  ScrollText,
  Calendar,
  Layers,
  FileCheck2,
  LayoutGrid,
  Users,
  Microscope,
  CalendarCheck,
  ExternalLink,
  School,
  LucideIcon,
  ShieldPlus,
  Wind,
  HeartPulse
} from "lucide-react";

import useAuth from "../../../contexts/AuthContext";
import MyAlliedApplications from "./MyApplications";
import { DEFAULT_ALLIED_COURSES } from "../constants/courses";

const ICON_MAP: Record<string, LucideIcon> = {
  Stethoscope,
  FlaskConical,
  ShieldPlus,
  Wind,
  HeartPulse,
};

// ─── Animation Configs ────────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

// ─── Tab Config ───────────────────────────────────────────────────────────────

interface TabConfig {
  id: string;
  label: string;
  Icon: LucideIcon;
  authRequired?: boolean;
}

const TABS: TabConfig[] = [
  { id: "overview", label: "Overview", Icon: LayoutGrid },
  { id: "admissions", label: "Admissions", Icon: GraduationCap },
  { id: "notices", label: "Notices", Icon: ScrollText },
  { id: "applications", label: "My Applications", Icon: FileCheck2, authRequired: true },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle }) => (
  <div className="mb-6">
    <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
    {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
  </div>
);

interface EmptyStateProps {
  message: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ message }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-slate-50">
    <Layers className="w-7 h-7" />
    <p className="text-sm">{message}</p>
  </div>
);

const SkeletonLoader: React.FC = () => (
  <div className="animate-pulse space-y-3 py-4">
    <div className="h-4 bg-slate-200 rounded w-2/3" />
    <div className="h-12 bg-slate-100 rounded" />
    <div className="h-12 bg-slate-100 rounded" />
  </div>
);

// ─── Panel: Overview ─────────────────────────────────────────────────────────

const OverviewPanel: React.FC = () => (
  <div className="space-y-10">
    {/* Courses */}
    <div>
      <SectionHeader
        title="Specialised disciplines offered"
        subtitle="Four-year undergraduate programmes with foundational and field laboratory training."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DEFAULT_ALLIED_COURSES.map((course) => {
          const IconComponent = ICON_MAP[course.iconName] || GraduationCap;
          return (
            <div
              key={course.name}
              className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-3 hover:border-blue-300 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 bg-slate-100 text-slate-700 rounded-lg flex items-center justify-center group-hover:bg-blue-50 group-hover:text-blue-700 transition-colors">
                  <IconComponent className="w-4 h-4" />
                </div>
                <span className="text-[11px] font-bold text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-full">
                  {course.shortCode}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-1 leading-snug group-hover:text-blue-900 transition-colors">
                  {course.name}
                </h4>
                <p className="text-xs text-slate-500 leading-relaxed">{course.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Academic Standards */}
    <div>
      <SectionHeader title="Academic standards" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { Icon: Users, title: "Elite clinicians", desc: "Direct training under seasoned medical faculty with AIIMS credentials." },
          { Icon: Microscope, title: "Modern labs", desc: "State-of-the-art diagnostic facilities with real-world simulation tools." },
          { Icon: CalendarCheck, title: "Structured tracks", desc: "Centrally managed timelines ensuring professional certification on schedule." },
        ].map(({ Icon, title, desc }) => (
          <div key={title} className="bg-slate-50 border border-slate-100 rounded-xl p-5">
            <div className="w-8 h-8 bg-white text-slate-500 border border-slate-200 rounded-lg flex items-center justify-center mb-4">
              <Icon className="w-4 h-4" />
            </div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">{title}</h4>
            <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Panel: Admissions ────────────────────────────────────────────────────────

interface ApplicationDates {
  startDate?: string;
  endDate?: string;
}

interface AdmissionItem {
  _id: string;
  title?: string;
  name?: string;
  applicationDates?: ApplicationDates;
  seats?: number;
  applicationLink?: string;
  admissionId?: string;
}

interface AdmissionsPanelProps {
  admissions: AdmissionItem[];
  isLoading: boolean;
  registrationClosed: boolean;
}

interface AdmissionsPanelProps {
  admissions: AdmissionItem[];
  isLoading: boolean;
  registrationClosed: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showArchive?: boolean;
  onShowArchiveChange?: (show: boolean) => void;
}

const AdmissionsPanel: React.FC<AdmissionsPanelProps> = ({
  admissions,
  isLoading,
  registrationClosed,
  page,
  totalPages,
  onPageChange,
  showArchive,
  onShowArchiveChange,
}) => {
  const { isStudentAdmin, isSuperAdmin } = useAuth();
  const isAdmin = isStudentAdmin || isSuperAdmin;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <SectionHeader
          title="Current admissions"
          subtitle="Open recruitment cycles for the current academic year."
        />
        {onShowArchiveChange && (
          <label className="inline-flex items-center cursor-pointer gap-2 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 transition-colors self-start sm:self-auto">
            <input
              type="checkbox"
              checked={showArchive}
              onChange={(e) => onShowArchiveChange(e.target.checked)}
              className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
            />
            <span className="text-xs font-semibold text-slate-700">Show Archive</span>
          </label>
        )}
      </div>
      {isLoading ? (
        <SkeletonLoader />
      ) : admissions.length > 0 ? (
        <div className="space-y-3">
          {admissions.map((adm) => {
            const now = new Date();
            const startDate = adm.applicationDates?.startDate ? new Date(adm.applicationDates.startDate) : null;
            const endDate = adm.applicationDates?.endDate ? new Date(adm.applicationDates.endDate) : null;

            const isUpcoming = startDate ? now < startDate : false;
            const isEnded = endDate ? now > endDate : false;
            const isClosed = registrationClosed || isEnded;
            const canApply = (!isUpcoming || isAdmin) && !isClosed;

            let buttonText = "Go to Applications";
            if (isUpcoming && !isAdmin) {
              buttonText = startDate
                ? `Starts on ${startDate.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                : "Opening Soon";
            } else if (isUpcoming && isAdmin) {
              buttonText = "Go to Applications (Admin Test)";
            } else if (isClosed) {
              buttonText = "Registration closed";
            }

            return (
              <div
                key={adm._id}
                className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:border-slate-300 transition-colors"
              >
                <div className="flex flex-col gap-2">
                  <h4 className="text-sm font-semibold text-slate-900 leading-snug">{adm.title || adm.name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {adm.applicationDates?.startDate && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-3 py-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(adm.applicationDates.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        {" – "}
                        {new Date(adm.applicationDates.endDate!).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                    {adm.seats && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-3 py-1">
                        <Users className="w-3 h-3" />
                        {adm.seats} seats
                      </span>
                    )}
                    {isUpcoming && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                        Opening Soon {isAdmin ? "(Admin Test Mode)" : ""}
                      </span>
                    )}
                  </div>
                </div>
                <a
                  href={adm.applicationLink}
                  className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors flex-shrink-0 ${!canApply
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed pointer-events-none"
                    : "bg-slate-900 text-white hover:bg-slate-700"
                    }`}
                >
                  {buttonText}
                  {canApply && <ArrowRight className="w-3.5 h-3.5" />}
                </a>
              </div>
            );
          })}

          {totalPages && totalPages > 1 && page && onPageChange && (
            <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-6">
              <p className="text-xs text-slate-500 font-medium">
                Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
                <span className="font-semibold text-slate-800">{totalPages}</span>
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => onPageChange(page - 1)}
                  className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  Previous
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => onPageChange(page + 1)}
                  className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <EmptyState message="No admissions are currently available." />
      )}
    </div>
  );
};

// ─── Panel: Notices ───────────────────────────────────────────────────────────

interface NoticeItem {
  title?: string;
  message?: string;
  date?: string;
  link?: string;
}

interface NoticesPanelProps {
  notices: NoticeItem[];
  isLoading: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showArchive?: boolean;
  onShowArchiveChange?: (show: boolean) => void;
}

const NoticesPanel: React.FC<NoticesPanelProps> = ({
  notices,
  isLoading,
  page,
  totalPages,
  onPageChange,
  showArchive,
  onShowArchiveChange,
}) => (
  <div>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
      <SectionHeader
        title="Latest notices"
        subtitle="Official communications from the admissions office."
      />
      {onShowArchiveChange && (
        <label className="inline-flex items-center cursor-pointer gap-2 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 transition-colors self-start sm:self-auto">
          <input
            type="checkbox"
            checked={showArchive}
            onChange={(e) => onShowArchiveChange(e.target.checked)}
            className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
          />
          <span className="text-xs font-semibold text-slate-700">Show Archive</span>
        </label>
      )}
    </div>
    {isLoading ? (
      <SkeletonLoader />
    ) : notices.length > 0 ? (
      <div className="space-y-4">
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {notices.map((notice, i) => (
            <a
              key={i}
              href={notice.link || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 px-5 py-4 bg-white hover:bg-slate-50 transition-colors cursor-pointer block"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 leading-relaxed">{notice.title || notice.message}</p>
                {notice.date && (
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(notice.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                )}
              </div>
              {notice.link && <ExternalLink className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-0.5" />}
            </a>
          ))}
        </div>

        {totalPages && totalPages > 1 && page && onPageChange && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-2">
            <p className="text-xs text-slate-500 font-medium">
              Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
              <span className="font-semibold text-slate-800">{totalPages}</span>
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => onPageChange(page - 1)}
                className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => onPageChange(page + 1)}
                className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    ) : (
      <EmptyState message="No new notices at this time." />
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AlliedLandingPage: React.FC = () => {
  const { apiRequest, isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [admissions, setAdmissions] = useState<AdmissionItem[]>([]);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination & Archive state
  const [admissionPage, setAdmissionPage] = useState(1);
  const [admissionTotalPages, setAdmissionTotalPages] = useState(1);
  const [showArchivedAdmissions, setShowArchivedAdmissions] = useState(false);

  const [noticePage, setNoticePage] = useState(1);
  const [noticeTotalPages, setNoticeTotalPages] = useState(1);
  const [showArchivedNotices, setShowArchivedNotices] = useState(false);

  const fetchAdmissions = async (page: number, showArchive: boolean) => {
    try {
      setIsLoading(true);
      const res = await apiRequest(`/admission-vacancies?page=${page}&limit=5&showArchive=${showArchive}`);
      const admData: AdmissionItem[] = res?.data || [];
      setAdmissions(
        admData.map((adm) => ({
          ...adm,
          applicationLink: `/allied/admission/${adm?.admissionId}/applications`,
        }))
      );
      setAdmissionTotalPages(res?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching admissions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotices = async (page: number, showArchive: boolean) => {
    try {
      setIsLoading(true);
      const res = await apiRequest(`/notifications?module=Student Admission&page=${page}&limit=5&showArchive=${showArchive}`);
      setNotices(res?.notices || []);
      setNoticeTotalPages(res?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching notices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "overview") {
      fetchAdmissions(1, false);
    } else if (activeTab === "admissions") {
      fetchAdmissions(admissionPage, showArchivedAdmissions);
    } else if (activeTab === "notices") {
      fetchNotices(noticePage, showArchivedNotices);
    }
  }, [activeTab, admissionPage, showArchivedAdmissions, noticePage, showArchivedNotices]);

  // Reset to overview if user logs out while on auth-only tab
  useEffect(() => {
    if (!isAuthenticated && activeTab === "applications") {
      setActiveTab("overview");
    }
  }, [isAuthenticated, activeTab]);

  const registrationClosed = (() => {
    if (!admissions[0]?.applicationDates?.endDate) return false;
    const end = new Date(admissions[0].applicationDates.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end < today;
  })();

  const heroStatus = registrationClosed
    ? { label: "Registration closed", className: "bg-red-50 text-red-700 border-red-200" }
    : admissions.length > 0
      ? { label: "Admissions active", className: "bg-green-50 text-green-700 border-green-200" }
      : { label: "No active admissions", className: "bg-slate-100 text-slate-500 border-slate-200" };

  const visibleTabs = TABS.filter((t) => !t.authRequired || isAuthenticated);

  return (
    <div className="w-full bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-20">
        {/* ── Hero ── */}
        <motion.section
          className="text-center mb-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants as any} className="flex justify-center mb-5">
            <div className="w-14 h-14 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm">
              <School className="w-6 h-6 text-slate-700" />
            </div>
          </motion.div>

          <motion.div variants={itemVariants as any} className="mb-4">
            <span className={`text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full border ${heroStatus.className}`}>
              {heroStatus.label}
            </span>
          </motion.div>

          <motion.div variants={itemVariants as any}>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-2 tracking-tight leading-tight">
              B.Sc. Allied Health Sciences
            </h1>
            <p className="text-xs font-semibold text-slate-400 tracking-widest uppercase mb-5">
              All India Institute of Medical Sciences, Mangalagiri
            </p>
            <p className="text-sm text-slate-500 max-w-xl mx-auto leading-relaxed">
              Advance your medical career inside India's premier healthcare infrastructure.
              Review programmes, monitor notices, and manage your applications.
            </p>
          </motion.div>
        </motion.section>

        {/* ── Tab Bar ── */}
        <motion.div
          variants={itemVariants as any}
          initial="hidden"
          animate="visible"
          className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-6 shadow-sm"
          role="tablist"
        >
          {visibleTabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => {
                setActiveTab(id);
                // Reset page on tab change
                setAdmissionPage(1);
                setNoticePage(1);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${activeTab === id
                ? "bg-slate-900 text-white font-medium shadow-sm"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </motion.div>

        {/* ── Tab Panels ── */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-8 shadow-sm min-h-[420px]">
          {activeTab === "overview" && <OverviewPanel />}

          {activeTab === "admissions" && (
            <AdmissionsPanel
              admissions={admissions}
              isLoading={isLoading}
              registrationClosed={registrationClosed}
              page={admissionPage}
              totalPages={admissionTotalPages}
              onPageChange={setAdmissionPage}
              showArchive={showArchivedAdmissions}
              onShowArchiveChange={setShowArchivedAdmissions}
            />
          )}

          {activeTab === "notices" && (
            <NoticesPanel
              notices={notices}
              isLoading={isLoading}
              page={noticePage}
              totalPages={noticeTotalPages}
              onPageChange={setNoticePage}
              showArchive={showArchivedNotices}
              onShowArchiveChange={setShowArchivedNotices}
            />
          )}

          {activeTab === "applications" && isAuthenticated && (
            <div>
              <SectionHeader
                title="My applications"
                subtitle="Track the status of your submitted applications."
              />
              <MyAlliedApplications />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlliedLandingPage;
